import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  inject,
  signal,
  output,
  viewChild,
  input,
  model,
  OnChanges
} from "@angular/core";
import { AbstractControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatAutocompleteTrigger, MatAutocompleteModule } from "@angular/material/autocomplete";
import { BaseFormGroupComponent } from "@app/shared/components/abstracts/base-component";
import { REGEX } from "@app/shared/constants/regex.constants";
import { Observable, Subject, debounceTime, distinctUntilChanged, exhaustMap, of, scan, startWith, switchMap, takeWhile, tap } from "rxjs";
import { TranslocoPipe } from "@jsverse/transloco";
import { MatOptionModule } from "@angular/material/core";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatButtonModule } from "@angular/material/button";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { AsyncPipe } from "@angular/common";
import { FunctionPipe } from "@app/shared/pipes/function.pipe";
import { BoldPipe } from "@app/shared/pipes/bold.pipe";
import { AutoFocusDirective } from "@app/shared/directives/auto-focus.directive";
import { ClickStopDirective } from "@app/shared/directives/click-stop.directive";
import { ScrollToElementDirective } from "@app/shared/directives/scroll-to-element.directive";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TypeAheadScrollDirective } from "@app/shared/components/type-ahead/type-ahead-scroll.directive";
import { ComponentChanges } from "@app/shared/models/on-changes.model";

@Component({
  selector: "app-type-ahead",
  templateUrl: "./type-ahead.component.html",
  styleUrls: ["./type-ahead.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    AutoFocusDirective,

    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    TypeAheadScrollDirective,
    MatOptionModule,
    AsyncPipe,
    TranslocoPipe,
    BoldPipe,
    FunctionPipe,
    ClickStopDirective,
    ScrollToElementDirective
  ]
})
export class TypeAheadComponent extends BaseFormGroupComponent implements OnInit, OnDestroy, OnChanges {
  data = model<any[]>();
  label = input<string>();
  controlName = input<string>();
  form = input<FormGroup>();
  valueName = input<string>();
  displayName = input<string>();
  displayName2 = input<string>();
  displayName3 = input<string>();
  displayName4 = input<string>();
  focus = input<boolean>();
  showAction = input<boolean>();
  boldValueName = input<string>(); // Mark row as Bolded if this value is true.
  actionIcon = input<string>();
  actionTooltip = input<string>();
  filterByValueName = input<string>();
  filterByValue = model<any>();
  emitValue = input<string>();
  clearValue = input<boolean>(true);
  customErrorMsg = input<string>();
  autoSelectFirst = input<boolean>(true); // Auto select the only record in the array if it has any.

  filterByValueChange = output<any>();
  selectionChanged = output();
  actionClicked = output<void>();
  listChanged = output<any>();
  emitValueChanged = output<string>();
  displayedValue = output<string>();

  trigger = viewChild<MatAutocompleteTrigger>("trigger");

  dataContext: any[] = [];
  filteredOptions = signal<any>([]);
  isBackspace: boolean;
  tab: undefined;

  private _destroyRef = inject(DestroyRef);
  private _nextPage = new Subject<void>();

  constructor() {
    super();
    this._addScrollEventListener();
  }

  ngOnInit(): void {
    this.setFilteredOptions();
  }

  ngOnDestroy() {
    this._removeScrollEventListener();
  }

  ngOnChanges(changes: ComponentChanges<this>) {
    if (changes.data) {
      this.dataContext = this.data() ?? [];
      this._setValue();
      if (this.filteredOptions()) {
        this.filteredOptions.set(this.dataContext);
      }
    }
  }

  openPanel() {
    this.trigger()?.openPanel();
  }

  closePanel() {
    this.trigger()?.closePanel();
  }

  /**
   * Clear the current value and open the autocomplete panel
   */
  clear() {
    this.control?.setValue(undefined);
    this.markDirty();
    this.onSelect();
  }

  /**
   * This is called multiple times after the scroll has reached the 80% threshold position.
   */
  onScroll() {
    this._nextPage.next();
  }

  onActionClicked() {
    this.actionClicked.emit();
  }

  markDirty() {
    if (this.control?.pristine) {
      this.control?.markAsDirty();
    }
  }

  /**
   * Validate current control value against array.
   * @returns
   */
  validate() {
    if (!this.control?.value) {
      return;
    }

    const record = this.dataContext?.find((x) => x[this.valueName()] === this.control?.value);
    if (!record) {
      this.control?.setErrors({ required: true });
    } else {
      this.control?.setErrors(null);
    }
  }

  checkForBold = (value: any): boolean => {
    if (!this.boldValueName()) {
      return false;
    }

    const foundValue: any = this.dataContext?.find((x) => x[this.valueName()] === value || this.control?.value);
    if (!foundValue) {
      return false;
    }

    return foundValue[this.boldValueName()];
  };

  /**
   * Select auto complete value and set it to passed in control and emit to parent that a selection was made
   * NOTE: This method runs AFTER autoCompleteDisplay is finished.
   */
  onSelect() {
    this.filterByValueChange.emit(this.filterByValue());
    this.selectionChanged.emit();
  }

  onKeyDown(event: KeyboardEvent) {
    this.isBackspace = event.code === "Backspace";
  }

  /**
   * Auto select record if display name or value matches a record
   */
  autoSelectValue() {
    if (this.isBackspace || !this.control?.value || this.control?.invalid) {
      return;
    }

    // Try and match display name
    const displayRecord = this.dataContext?.find(
      (x) => x[this.displayName()].toString().toLowerCase() === this.control?.value?.toString()?.toLowerCase()
    );
    if (displayRecord) {
      this.control?.setValue(displayRecord[this.valueName()], { emitEvent: false });
      this.onSelect();
      return;
    }

    // Try and match value name
    const valueRecord = this.dataContext?.find(
      (x) => x[this.valueName()].toString().toLowerCase() === this.control?.value?.toString()?.toLowerCase()
    );
    if (valueRecord) {
      this.control?.setValue(valueRecord[this.valueName()], { emitEvent: false });
    }
  }

  /**
   * Display value in the input for selected record
   */
  autoCompleteDisplay(value: any, emit: boolean = true): string {
    this.setFilterByValue(value);

    const foundValue: any = this.dataContext?.find((x) => {
      return x[this.valueName()] === value && (this.filterByValueName() ? x[this.filterByValueName()] === this.filterByValue() : true);
    });

    this.emitValue() ? this.emitValueChanged.emit(foundValue ? foundValue[this.emitValue()] : "") : "";

    if (!foundValue) {
      return "";
    }

    return this.getDisplayName(foundValue, emit);
  }

  /**
   * Set filter value so we can use it to find the correct display value and emit the correct value
   * @param value
   */
  setFilterByValue(value: string) {
    if (value && this.filterByValueName()) {
      const data = this.dataContext.find((x) => x[this.valueName()] === value);
      this.filterByValue.set(data ? data[this.filterByValueName()] : "");
    } else {
      this.filterByValue.set("");
    }
  }

  /**
   * Build string for display name
   * @param foundValue
   * @returns
   */
  getDisplayName = (foundValue: any, emit: boolean = true) => {
    let dn = foundValue[this.displayName()];
    if (emit) {
      this.displayedValue.emit(dn);
    }

    if (this.displayName2()) {
      const dn2 = foundValue[this.displayName2()];
      const dn3 = foundValue[this.displayName3()];
      if (dn2) {
        dn += ` (${dn2}${dn3 ? " - " + dn3 : ""})`;
      }
    }

    const displayName4 = this.displayName4();
    if (displayName4) {
      const dn4 = foundValue[displayName4];
      if (dn4) {
        dn += ` - ${dn4}`;
      }
    }
    return dn;
  };

  setFilteredOptions() {
    this.control?.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        debounceTime(this.getDebounce()),
        startWith(0),
        distinctUntilChanged(),
        switchMap(() => {
          // Reset the page with every new search
          let currentPage = 1;
          return this.nextPage.pipe(
            startWith(currentPage),
            // Until the method responds, ignore NextPage requests.
            exhaustMap(() => this.getData(this.control?.value, currentPage)),
            tap(() => currentPage++),
            // This is a custom operator because we also need the last emitted value.
            // Stop if there are no more pages, or no results at all for the current search text.
            takeWhile((p) => p.length > 0, true),
            scan((allRecords, newRecords) => allRecords.concat(newRecords), [])
          );
        })
      )
      .subscribe({
        next: (data) => {
          this.filteredOptions.set(data);
        },
        error: () => {
          this.filteredOptions.set([]);
        }
      });
  }

  /**
   * Return data based on if asyncFunction is passed in
   * @param filter
   * @param page
   * @returns
   */
  getData(filter: string, page: number) {
    return this._filter(filter, page);
  }

  /**
   * Store data from filter methods and validate
   * @param data
   */
  setData(data: any[], isAsync?: boolean) {
    // When the method is async we will get new data every call
    // otherwise all data is loaded up front and we only need to set it once.
    if (isAsync || !this.dataContext) {
      this.dataContext = data;
      this.listChanged.emit(this.dataContext);
    }
    this.autoSelectValue();
    this.validate();
  }

  /**
   * Slice the array for the allowed amount to be rendered at one time.
   * Value is set on _maxRecordsShown
   * @param data
   * @param page
   * @returns
   */
  sliceRecords(data: any, page: number) {
    const take = 100;
    const skip = page > 0 ? (page - 1) * take : 0;
    return data.slice(skip, skip + take);
  }

  get control(): AbstractControl {
    return this.form()?.get([this.controlName()]);
  }

  get nextPage(): Subject<any> {
    return this._nextPage;
  }

  /**
   * Filter the list to find a matching value based on user input.
   * Value string is split into an array based on spaces and commas.
   * Filter results will include any values in the array.
   * @param value
   */
  private _filter(value: string, page: number): Observable<any[]> {
    let filtered = this.dataContext;

    if (value) {
      filtered = this.dataContext
        ?.map((x) => x)
        .filter((option) => {
          return value
            ?.toString()
            ?.split(REGEX.SPACE_COMMA_FILTER)
            ?.every((v) => {
              return Object.values(option).some((value) => value?.toString()?.toLowerCase()?.includes(v?.toString()?.toLowerCase()));
            });
        });
    }
    this.setData(filtered);
    return of(this.sliceRecords(filtered, page));
  }

  /**
   * Prevent open autocomplete scrolling off screen
   * @see https://github.com/angular/components/issues/10079#issuecomment-619887760
   */
  private _addScrollEventListener() {
    window.addEventListener("scroll", this._scrollEvent, true);
  }

  private _removeScrollEventListener() {
    window.removeEventListener("scroll", this._scrollEvent, true);
  }

  private _scrollEvent = () => {
    if (this.trigger()?.panelOpen) {
      this.trigger().updatePosition();
    }
  };

  private _setValue() {
    if (this.control) {
      if (this.clearValue() && !this.autoSelectFirst()) {
        this.control?.setValue("", { emitEvent: false });
        return;
      }

      if (this.autoSelectFirst() && !this.control?.value) {
        this.control?.setValue(this.dataContext?.length === 1 ? this.dataContext[0][this.valueName()] : "", {
          emitEvent: false
        });
        this.onSelect();
      } else {
        this.control?.setValue(this.control?.value, { emitEvent: false });
      }
    }
  }
}
